import type { ReactNode } from 'react';
import Card from './Card';

interface Column<T> {
  key: keyof T;
  header: string;
  render?: (item: T) => ReactNode;
}

interface TableProps<T extends { id: string }> {
  columns: Array<Column<T>>;
  data: T[];
  loading?: boolean;
  emptyLabel?: string;
}

export default function Table<T extends { id: string }>({ columns, data, loading, emptyLabel = 'Sem dados para exibir.' }: TableProps<T>) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/60">
            <tr>
              {columns.map((column) => (
                <th key={String(column.key)} className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 4 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="border-t border-borderLight dark:border-borderDark">
                    <td colSpan={columns.length} className="px-4 py-3">
                      <div className="h-5 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                    </td>
                  </tr>
                ))
              : null}

            {!loading && data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-300">
                  {emptyLabel}
                </td>
              </tr>
            ) : null}

            {!loading
              ? data.map((item) => (
                  <tr key={item.id} className="border-t border-borderLight transition hover:bg-slate-50 dark:border-borderDark dark:hover:bg-slate-900/40">
                    {columns.map((column) => (
                      <td key={String(column.key)} className="px-4 py-3 text-slate-700 dark:text-slate-200">
                        {column.render ? column.render(item) : String(item[column.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
